import dayjs, { type Dayjs } from '@/config/dayjs';
import type { NetWorthChangePeriod } from '@/interfaces/netWorthChanges';
import { APP_DATE_FORMAT, formatAppDate, parseAppDate } from '@/utils/date';

/**
 * The comparison presets the legacy Biggest Net Worth Changes widget offers,
 * in its order (`Shared/Helpers/DateRanges.getShortRanges`).
 */
export enum NetWorthChangeRange {
  OneDay = '1 Day',
  Last5Days = 'Last 5 Days',
  Last7Days = 'Last 7 Days',
  ThisMonth = 'This Month',
  ThisQuarter = 'This Quarter',
  ThisYear = 'This Year',
}

export const NET_WORTH_CHANGE_RANGE_ORDER: readonly NetWorthChangeRange[] = [
  NetWorthChangeRange.OneDay,
  NetWorthChangeRange.Last5Days,
  NetWorthChangeRange.Last7Days,
  NetWorthChangeRange.ThisMonth,
  NetWorthChangeRange.ThisQuarter,
  NetWorthChangeRange.ThisYear,
];

export const DEFAULT_NET_WORTH_CHANGE_RANGE = NetWorthChangeRange.ThisQuarter;

/**
 * Where the fallback lands when the default has no span yet -- on the second
 * day of a quarter, This Quarter runs from yesterday to yesterday.
 */
const FALLBACK_NET_WORTH_CHANGE_RANGE = NetWorthChangeRange.Last7Days;

/**
 * The latest date a comparison can end on. Balances are written by the
 * nightly job, so today's do not exist yet; every preset ends on yesterday.
 */
export function latestComparableDate(today: Dayjs = dayjs()): Dayjs {
  return today.startOf('day').subtract(1, 'day');
}

/** First day of the calendar quarter `date` falls in. */
function startOfQuarter(date: Dayjs): Dayjs {
  const firstMonthOfQuarter = Math.floor(date.month() / 3) * 3;
  return date.month(firstMonthOfQuarter).startOf('month');
}

/** A comparison's dates in the app's `MM/DD/YYYY`, for state and the query. */
function toPeriod(start: Dayjs, end: Dayjs): NetWorthChangePeriod {
  return {
    startDate: formatAppDate(start, { format: APP_DATE_FORMAT }),
    endDate: formatAppDate(end, { format: APP_DATE_FORMAT }),
  };
}

/**
 * Where a preset starts, counted back from the day it ends on. The calendar
 * presets take their month, quarter or year from that end day, not from
 * today, exactly as legacy's `getShortRanges` does: on September 1 the end is
 * August 31, so This Month is August 1-31 -- the last month with balances --
 * rather than a month with nothing in it yet.
 */
function rangeStart(range: NetWorthChangeRange, end: Dayjs): Dayjs {
  switch (range) {
    case NetWorthChangeRange.OneDay:
      return end.subtract(1, 'day');
    case NetWorthChangeRange.Last5Days:
      return end.subtract(5, 'day');
    case NetWorthChangeRange.Last7Days:
      return end.subtract(7, 'day');
    case NetWorthChangeRange.ThisMonth:
      return end.startOf('month');
    case NetWorthChangeRange.ThisQuarter:
      return startOfQuarter(end);
    case NetWorthChangeRange.ThisYear:
      return end.startOf('year');
  }
}

/**
 * Resolves a preset to the dates it compares, ending on the latest comparable
 * date. Returns null when the preset has no span on that day -- This Month on
 * the 2nd, say, would compare the 1st against itself.
 */
export function resolveNetWorthChangeRange(
  range: NetWorthChangeRange,
  today: Dayjs = dayjs(),
): NetWorthChangePeriod | null {
  const end = latestComparableDate(today);
  const start = rangeStart(range, end);
  if (!start.isBefore(end, 'day')) return null;
  return toPeriod(start, end);
}

/**
 * The presets that have a span today, in display order. Legacy drops the rest
 * rather than offering a range that compares a day against itself.
 */
export function availableNetWorthChangeRanges(
  today: Dayjs = dayjs(),
): NetWorthChangeRange[] {
  return NET_WORTH_CHANGE_RANGE_ORDER.filter(
    (range) => resolveNetWorthChangeRange(range, today) !== null,
  );
}

/**
 * The preset the widget opens on: This Quarter, unless the quarter is too new
 * to have a span, in which case Last 7 Days.
 */
export function defaultNetWorthChangeRange(
  today: Dayjs = dayjs(),
): NetWorthChangeRange {
  if (resolveNetWorthChangeRange(DEFAULT_NET_WORTH_CHANGE_RANGE, today)) {
    return DEFAULT_NET_WORTH_CHANGE_RANGE;
  }
  return FALLBACK_NET_WORTH_CHANGE_RANGE;
}

/** Whether a string names one of the presets, for reading a stored choice. */
export function isNetWorthChangeRange(
  value: string | null | undefined,
): value is NetWorthChangeRange {
  return NET_WORTH_CHANGE_RANGE_ORDER.some((range) => range === value);
}

/**
 * Normalises a custom range before it is asked for. An end date past the
 * latest comparable date is pulled back to it -- there are no balances for it,
 * so comparing against it would read as every client falling to zero. Returns
 * null when what is left has no span, or when either date does not parse.
 */
export function clampNetWorthChangePeriod(
  period: NetWorthChangePeriod,
  today: Dayjs = dayjs(),
): NetWorthChangePeriod | null {
  const start = parseAppDate(period.startDate);
  const requestedEnd = parseAppDate(period.endDate);
  if (!start || !requestedEnd) return null;

  const latest = latestComparableDate(today);
  const end = requestedEnd.isAfter(latest, 'day') ? latest : requestedEnd;
  if (!start.isBefore(end, 'day')) return null;
  return toPeriod(start, end);
}
